import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';
import { IScoreboardSettings, ISelectableTag, ITest } from '../../types';
import { queryHashGenerator } from '../../../utils/queryHash';

/**
 * Checks if a test matches the team/group filters in the scoreboard settings.
 * Uses AND logic: when both team and group filters are set, the test must
 * match both. This allows group filtering to narrow within a selected team.
 */
export const testMatchesFilters = (
  test: ITest,
  settings: IScoreboardSettings | undefined
): boolean => {
  if (!test) return false;
  const hasTeamFilter = settings?.filteredTeams?.length > 0;
  const hasGroupFilter = settings?.filteredGroups?.length > 0;
  if (!hasTeamFilter && !hasGroupFilter) return true;

  if (
    hasTeamFilter &&
    !test?.teams?.some((tId: string): boolean =>
      settings.filteredTeams.includes(tId)
    )
  ) {
    return false;
  }
  if (
    hasGroupFilter &&
    !test?.groups?.some((gId: string): boolean =>
      settings.filteredGroups.includes(gId)
    )
  ) {
    return false;
  }
  return true;
};

/**
 * Generates the queryHash for the current scoreboard settings.
 */
export const getScoreboardQueryHash = (
  settings: IScoreboardSettings | undefined
): string | undefined => {
  if (!settings?.testType) return undefined;
  return queryHashGenerator(
    settings.testType.value,
    settings.tags.map(
      (selectableTag: ISelectableTag): string => selectableTag.value
    )
  );
};

/**
 * Builds a ranked leaderboard from tests: filters by queryHash,
 * sorts by metric direction, deduplicates by athlete, limits to 100.
 */
export const buildScoreboardLeaderboard = (
  tests: ITest[],
  settings: IScoreboardSettings | undefined,
  metricKey: string | undefined
): ITest[] => {
  if (!metricKey || isEmpty(settings?.testType)) return [];

  const queryHash = getScoreboardQueryHash(settings);
  if (!queryHash) return [];

  const testsThatMatch = tests.reduce((acc: ITest[], item: ITest): ITest[] => {
    if (item[metricKey] && item.queryHash === queryHash) {
      acc.push(item);
    }
    return acc;
  }, []);

  const sortedData = sortBy(testsThatMatch, (item: ITest): number => {
    const direction = item[metricKey].direction;
    if (direction === 1) return -item[metricKey].value;
    if (direction === -1) return item[metricKey].value;
    return Math.abs(0 - Math.abs(item[metricKey].value));
  });

  return uniqBy(sortedData, (item: ITest): string => item.athlete?.id).slice(
    0,
    100
  );
};

/**
 * Finds the most recent test matching the current metric and queryHash.
 * Sorts by timestamp descending and returns the first match.
 */
export const findLatestTest = (
  tests: ITest[],
  settings: IScoreboardSettings | undefined,
  metricKey: string | undefined
): ITest | null => {
  if (!metricKey || isEmpty(settings?.testType)) return null;

  const queryHash = getScoreboardQueryHash(settings);
  if (!queryHash) return null;

  const filtered = tests.filter(
    (test: ITest): boolean => !!test[metricKey] && test.queryHash === queryHash
  );

  const sorted = sortBy(
    filtered,
    (item: ITest): number => -(item.timestamp || 0)
  );

  return sorted[0] ?? null;
};
